<template>
  <div class="videos-page">
    <van-nav-bar fixed placeholder class="page-nav" :border="false">
      <template #title>
        <span class="page-title">{{ $t('ui.video.title') }}</span>
      </template>
      <template #right>
        <van-icon name="home-o" class="home-icon" @click="$router.push('/Home')" />
      </template>
    </van-nav-bar>

    <div class="page-content">
      <section v-if="videoClasses.length" class="category-section">
        <div class="category-scroll">
          <button
            v-for="(item, index) in videoClasses"
            :key="item.id"
            type="button"
            class="category-chip"
            :class="{ active: navSel === index }"
            @click="changeClass(item, index)"
          >
            {{ item.name }}
          </button>
        </div>
      </section>

      <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
        <van-list
          v-model="loading"
          :finished="finished"
          finished-text=""
          @load="onLoad"
        >
          <section v-if="normalizedVideos.length" class="videos-grid">
            <article
              v-for="item in normalizedVideos"
              :key="item.renderKey"
              class="video-card"
              @click="toPlayVideo(item.id)"
            >
              <div class="video-cover-wrap">
                <img :src="item.vod_pic" :alt="item.vod_name" class="video-cover">
                <img src="https://m.sincereloves.online/icon/lives.gif" alt="live" class="live-icon">
                <div class="play-pill">
                  <van-icon name="play-circle-o" />
                  <span>{{ item.displayCount }}</span>
                </div>
              </div>

              <div class="video-meta">
                <div class="video-title">{{ item.vod_name }}</div>
              </div>
            </article>
          </section>

          <section v-else-if="!loading" class="empty-section">
            <img src="https://m.sincereloves.online/logo.png" alt="empty" class="empty-logo">
            <p>{{ $t('ui.video.empty') }}</p>
          </section>
        </van-list>
      </van-pull-refresh>
    </div>

  </div>
</template>

<script>
import { Toast } from 'vant';
import { fetchUserInfo, getVideoBanMessage } from '../../utils/videoAccess';

const DEFAULT_CLASS_ID = 888;

export default {
  data() {
    return {
      navSel: 0,
      loading: false,
      finished: false,
      refreshing: false,
      scrollbarStyleId: 'video-scrollbar-style',
      videoClasses: [],
      data: [],
      page: 0,
      id: DEFAULT_CLASS_ID
    };
  },
  computed: {
    normalizedVideos() {
      const seenIds = new Set();
      return this.data.reduce((result, item, index) => {
        const safeItem = item && typeof item === 'object' ? item : {};
        const uniqueId = safeItem.id || safeItem.vod_id || `video-${index}`;

        if (seenIds.has(uniqueId)) {
          return result;
        }
        seenIds.add(uniqueId);

        const count = Number(safeItem.count || safeItem.vod_score_num || 0);

        result.push({
          ...safeItem,
          renderKey: `${uniqueId}-${index}`,
          vod_pic: this.normalizeAssetUrl(safeItem.vod_pic || safeItem.cover),
          displayCount: count > 0 ? count : 999
        });

        return result;
      }, []);
    }
  },
  methods: {
    normalizeAssetUrl(url) {
      if (!url || typeof url !== 'string') {
        return '';
      }

      const origin = 'https://admin699.ltalk6.com';
      const normalizedUrl = url.trim();

      if (/^https?:\/\/127\.0\.0\.1\//.test(normalizedUrl)) {
        return normalizedUrl.replace(/^https?:\/\/127\.0\.0\.1\//, `${origin}/`);
      }

      if (/^https?:\/\/localhost:8081\//.test(normalizedUrl)) {
        return normalizedUrl.replace(/^https?:\/\/localhost:8081\//, `${origin}/`);
      }

      if (/^https?:\/\/[^/]*gxcg01\.buzz\//i.test(normalizedUrl)) {
        return normalizedUrl.replace(/^https?:\/\/[^/]*gxcg01\.buzz\//i, `${origin}/`);
      }

      if (normalizedUrl.startsWith('/')) {
        return `${origin}${normalizedUrl}`;
      }

      if (!/^https?:\/\//i.test(normalizedUrl)) {
        return `${origin}/${normalizedUrl.replace(/^\.?\//, '')}`;
      }

      return normalizedUrl;
    },
    onLoad(isRefresh = false) {
      if (isRefresh) {
        this.page = 0;
        this.data = [];
      }

      this.loading = true;

      this.getVideoList().then(() => {
        this.loading = false;
        if (this.refreshing) {
          this.refreshing = false;
          Toast(this.$t('ui.video.refresh_done'));
        }
      }).catch(() => {
        this.loading = false;
        this.refreshing = false;
      });
    },
    onRefresh() {
      this.finished = false;
      this.onLoad(true);
    },
    changeClass(item, index) {
      if (this.id === item.id && this.navSel === index && this.data.length) {
        return;
      }

      this.navSel = index;
      this.id = item.id;
      this.finished = false;
      this.data = [];
      this.page = 0;
      this.onLoad(true);
    },
    toPlayVideo(id) {
      this.ensureVideoAccess().then((allowed) => {
        if (!allowed) {
          return;
        }
        this.$router.push({ path: '/PlayVideo', query: { id } });
      });
    },
    ensureVideoAccess(showToast = true) {
      if (!localStorage.getItem('token')) {
        this.$router.push({ path: '/Login' });
        return Promise.resolve(false);
      }

      return fetchUserInfo().then((userInfo) => {
        const message = getVideoBanMessage(userInfo, this.$t.bind(this));
        if (message) {
          if (showToast) {
            Toast(message);
          }
          return false;
        }
        return true;
      });
    },
    hideRootScrollbar() {
      if (typeof document === 'undefined') {
        return;
      }

      const root = document.documentElement;
      const body = document.body;

      root.style.setProperty('scrollbar-width', 'none');
      body.style.setProperty('scrollbar-width', 'none');
      root.style.setProperty('-ms-overflow-style', 'none');
      body.style.setProperty('-ms-overflow-style', 'none');

      if (!document.getElementById(this.scrollbarStyleId)) {
        const style = document.createElement('style');
        style.id = this.scrollbarStyleId;
        style.textContent = `
html::-webkit-scrollbar,
body::-webkit-scrollbar {
  width: 0 !important;
  height: 0 !important;
  display: none !important;
}
        `;
        document.head.appendChild(style);
      }
    },
    restoreRootScrollbar() {
      if (typeof document === 'undefined') {
        return;
      }

      const root = document.documentElement;
      const body = document.body;
      const style = document.getElementById(this.scrollbarStyleId);

      root.style.removeProperty('scrollbar-width');
      body.style.removeProperty('scrollbar-width');
      root.style.removeProperty('-ms-overflow-style');
      body.style.removeProperty('-ms-overflow-style');

      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    },
    getVideoList() {
      return new Promise((resolve, reject) => {
        this.$http({
          method: 'get',
          data: { id: this.id, page: this.page },
          url: 'video_list'
        }).then((res) => {
          const list = res && res.data && Array.isArray(res.data.data) ? res.data.data : [];
          if (list.length) {
            this.data = this.data.concat(list);
            this.page += 1;
          } else {
            this.finished = true;
          }
          resolve();
        }).catch((error) => {
          reject(error);
        });
      });
    },
    getVideoClass() {
      this.$http({
        method: 'get',
        url: 'video_class'
      }).then((res) => {
        const lang = localStorage.getItem('lang');
        const classList = Array.isArray(res.data) ? res.data.slice() : [];
        classList.unshift({ id: DEFAULT_CLASS_ID, name: this.$t('ui.video.class_latest') });

        this.videoClasses = classList.map((item, index) => {
          if (index === 0 && item.id === DEFAULT_CLASS_ID) {
            return {
              ...item,
              name: this.$t('ui.video.class_latest')
            };
          }

          const parts = typeof item.name === 'string' ? item.name.split(',') : [];
          let localizedName = '';

          if (lang == 'kr') {
            localizedName = parts[0];
          } else if (lang == 'en') {
            localizedName = parts[1];
          } else if (lang == 'ja') {
            localizedName = parts[2];
          } else {
            localizedName = parts[3];
          }

          return {
            ...item,
            name: localizedName || parts[0] || item.name || this.$t('ui.video.class_fallback', { id: item.id })
          };
        });

        this.onLoad(true);
      });
    }
  },
  mounted() {
    this.hideRootScrollbar();
    this.ensureVideoAccess();
  },
  beforeDestroy() {
    this.restoreRootScrollbar();
  },
  created() {
    this.getVideoClass();
  }
};
</script>

<style lang="less" scoped>
.videos-page {
  min-height: 100vh;
  background: #000;
  color: #fff;
}

.page-nav {
  min-height: 98px;
  background: #1c1c1e;
}

::v-deep .page-nav .van-nav-bar {
  background: #1c1c1e;
  box-shadow: none;
}

::v-deep .page-nav .van-nav-bar__content {
  height: 88px;
}

::v-deep .page-nav .van-nav-bar__left,
::v-deep .page-nav .van-nav-bar__right {
  padding: 0 18px;
}

.page-title {
  color: #e6b98a;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.home-icon {
  color: #e6b98a;
  font-size: 38px;
}

.page-content {
  padding: 18px 12px 136px;
}

.category-section {
  margin-bottom: 24px;
}

.category-scroll {
  display: flex;
  gap: 14px;
  overflow-x: auto;
  padding-bottom: 6px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.category-scroll::-webkit-scrollbar {
  display: none;
}

.category-chip {
  flex-shrink: 0;
  min-width: 118px;
  padding: 16px 24px;
  border: 1px solid #766350;
  border-radius: 999px;
  background: linear-gradient(180deg, #2a2b30, #17181d);
  color: #c7c7c7;
  font-size: 20px;
  font-weight: 700;
}

.category-chip.active {
  background: linear-gradient(180deg, #fde3ca, #e7b486);
  border-color: transparent;
  color: #68302c;
}

.videos-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

.video-card {
  overflow: hidden;
  border: 2px solid #766350;
  border-radius: 22px;
  background: linear-gradient(180deg, #1b1c21, #0f1015);
}

.video-cover-wrap {
  position: relative;
  overflow: hidden;
  min-height: 340px;
}

.video-cover {
  display: block;
  width: 100%;
  height: 340px;
  object-fit: cover;
}

.live-icon {
  position: absolute;
  top: 14px;
  left: 14px;
  width: 34px;
  height: 34px;
  object-fit: contain;
}

.play-pill {
  position: absolute;
  right: 12px;
  bottom: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.58);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
}

.video-meta {
  padding: 14px 14px 16px;
}

.video-title {
  overflow: hidden;
  color: #f2d1ad;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.4;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.empty-section {
  display: flex;
  min-height: 52vh;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #bd9777;
  text-align: center;
}

.empty-logo {
  width: 72px;
  height: 72px;
  margin-bottom: 14px;
  object-fit: contain;
}

.empty-section p {
  margin: 0;
  color: #f2d1ad;
  font-size: 18px;
  font-weight: 700;
}

.empty-section span {
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.5;
}

</style>
